import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useEinstellung } from '../contexts/EinstellungenContext';
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronLeft, ChevronRight, Columns3, Search, X } from 'lucide-react';

/**
 * Suchen, filtern, sortieren — einmal gebaut für alle Tabellen des PC.
 *
 * Vorher war jede Tabelle eine reine Liste in der Reihenfolge, die das Backend
 * zurückgab. Bei drei Servern geht das; bei dreißig Instanzen sucht man mit
 * Strg+F im Browser, und stillgelegte Einträge stehen für immer zwischen den
 * laufenden.
 *
 * Bewusst im Browser und nicht im Backend: die Listen im PC sind klein (Server,
 * Kunden, Instanzen — Größenordnung Hunderte), und eine Sortierung, die eine
 * Abfrage auslöst, fühlt sich träge an. Wird eine Liste einmal wirklich groß,
 * ist das der Moment, es zu verschieben — nicht vorher.
 *
 * DIE SEITENAUFTEILUNG STEHT HINTER SUCHE UND SORTIERUNG, nicht davor. Das ist
 * die ganze Begründung, warum sie hier liegt und nicht im Backend: eine
 * Blätterleiste über einer serverseitig geschnittenen Liste durchsucht nur die
 * Seite, die gerade geladen ist. Wer dann nach einer Domain sucht, die auf
 * Seite drei steht, bekommt „nichts gefunden" — und das ist ein schlimmerer
 * Fehler als eine lange Tabelle, weil er wie eine Antwort aussieht.
 *
 * Das Audit-Log macht es deshalb umgekehrt und bleibt serverseitig: es wächst
 * unbegrenzt und hat keine Freitextsuche, die falsch werden könnte.
 */

export type Richtung = 'auf' | 'ab';

export interface SpaltenDefinition<T> {
  /** Schlüssel, unter dem die Spalte in der Sortierung steht. */
  key: string;
  label: string;
  /** Der Wert, nach dem sortiert wird. Fehlt er, ist die Spalte nicht sortierbar. */
  wert?: (zeile: T) => string | number | Date | null | undefined;
  /** Rechtsbündig für Zahlen. */
  numerisch?: boolean;
  /** Anfangs ausgeblendet; über die Spaltenauswahl einzuschalten. */
  versteckt?: boolean;
  /** Lässt sich nicht ausblenden — die Spalte, an der man die Zeile erkennt. */
  fest?: boolean;
}

export interface FilterDefinition<T> {
  key: string;
  label: string;
  optionen: { wert: string; label: string }[];
  /** Trifft die Zeile auf den gewählten Wert zu? */
  passt: (zeile: T, wert: string) => boolean;
}

interface Optionen<T> {
  zeilen: T[];
  spalten: SpaltenDefinition<T>[];
  /** Felder, über die die Freitextsuche läuft. */
  suchfelder?: (zeile: T) => (string | null | undefined)[];
  filter?: FilterDefinition<T>[];
  /** Spalte, nach der anfangs sortiert wird. */
  startSortierung?: { key: string; richtung: Richtung };
  /** Zeilen je Seite. `0` schaltet die Seitenaufteilung ab. */
  proSeiteStart?: number;
  /**
   * Unter diesem Namen merkt sich der PC Suche, Filter, Sortierung und
   * Seitengröße für die angemeldete Person, bis sie sie selbst zurücksetzt.
   * Ohne Namen gilt der Zustand nur, solange die Seite offen ist.
   */
  speichern?: string;
}

/** Was je Tabelle gemerkt wird */
interface GemerkterZustand {
  suche?: string;
  filter?: Record<string, string>;
  sortKey?: string | null;
  richtung?: Richtung;
  proSeite?: number;
  /** Nur Abweichungen von der Vorgabe der Spalte: `true` gezeigt, `false` aus. */
  spalten?: Record<string, boolean>;
}

/**
 * Was eine Blätterleiste braucht. Getrennt von der Tabellensteuerung, damit
 * auch serverseitig geblätterte Ansichten (Audit-Log) dieselbe Leiste benutzen
 * können, statt sich eine eigene zu bauen — vorher hatte das Audit-Log eine
 * eigene, und sie war kaputt.
 */
export interface Blaettern {
  seite: number;
  seiten: number;
  setSeite: (n: number) => void;
  /** Nummer der ersten und letzten angezeigten Zeile, 1-basiert, für „12–36 von 423". */
  von: number;
  bis: number;
  gesamt: number;
  proSeite: number;
  /** Fehlt bei serverseitiger Aufteilung mit fester Seitengröße. */
  setProSeite?: (n: number) => void;
}

export interface TabellenSteuerung<T> {
  /** Nur die Zeilen der aktuellen Seite. */
  sichtbar: T[];
  /** Nach Suche und Filtern, vor der Seitenaufteilung. */
  gefiltert: number;
  /**
   * Alle Zeilen, die Suche und Filter überstanden haben — nicht nur die
   * aktuelle Seite. Für alles, was sich auf „was gerade zu sehen ist" bezieht
   * und über eine Seite hinausgeht: der CSV-Export etwa soll das ausgeben, was
   * auf dem Bildschirm steht, und nicht die 25 Zeilen davon.
   */
  alleGefilterten: T[];
  gesamt: number;
  blaettern: Blaettern;
  suche: string;
  setSuche: (s: string) => void;
  sortKey: string | null;
  richtung: Richtung;
  sortieren: (key: string) => void;
  filterWerte: Record<string, string>;
  setFilter: (key: string, wert: string) => void;
  zuruecksetzen: () => void;
  /** Ist irgendetwas eingeschränkt? Für den "Alles zeigen"-Knopf. */
  eingeschraenkt: boolean;
  /** Die Spalten, die gerade zu sehen sind, in ihrer Reihenfolge. */
  sichtbareSpalten: SpaltenDefinition<T>[];
  zeigt: (key: string) => boolean;
  setSpalte: (key: string, an: boolean) => void;
}

export function useTabelle<T>({
  zeilen,
  spalten,
  suchfelder,
  filter = [],
  startSortierung,
  proSeiteStart = 25,
  speichern,
}: Optionen<T>): TabellenSteuerung<T> {
  const [gemerkt, merken] = useEinstellung<GemerkterZustand | null>(speichern ? `tabelle.${speichern}` : undefined, null);
  const start = gemerkt ?? {};
  const [suche, setSuche] = useState(start.suche ?? '');
  const [seite, setSeite] = useState(1);
  const [proSeite, setProSeite] = useState(start.proSeite ?? proSeiteStart);
  const [sortKey, setSortKey] = useState<string | null>(
    start.sortKey !== undefined ? start.sortKey : (startSortierung?.key ?? null),
  );
  const [richtung, setRichtung] = useState<Richtung>(start.richtung ?? startSortierung?.richtung ?? 'auf');
  const [filterWerte, setFilterWerte] = useState<Record<string, string>>(start.filter ?? {});
  const [spaltenWahl, setSpaltenWahl] = useState<Record<string, boolean>>(start.spalten ?? {});

  // Merken, sobald sich etwas ändert. Was der Vorgabe entspricht, wird nicht
  // gespeichert — so bleibt nur, was die Person wirklich eingestellt hat.
  const filterGesetzt = Object.fromEntries(Object.entries(filterWerte).filter(([, w]) => w));
  const zustandText = JSON.stringify([suche, filterGesetzt, sortKey, richtung, proSeite, spaltenWahl]);
  useEffect(() => {
    if (!speichern) return;
    const z: GemerkterZustand = {};
    if (suche) z.suche = suche;
    if (Object.keys(filterGesetzt).length) z.filter = filterGesetzt;
    if (sortKey !== (startSortierung?.key ?? null) || richtung !== (startSortierung?.richtung ?? 'auf')) {
      z.sortKey = sortKey;
      z.richtung = richtung;
    }
    if (proSeite !== proSeiteStart) z.proSeite = proSeite;
    if (Object.keys(spaltenWahl).length) z.spalten = spaltenWahl;
    merken(Object.keys(z).length ? z : null);
    // merken wechselt bei jedem Zeichnen seine Identität; maßgeblich ist der Zustand.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zustandText, speichern]);

  const sortieren = (key: string) => {
    if (sortKey === key) {
      // Dritter Klick hebt die Sortierung auf — zurück zur Reihenfolge des
      // Backends, die bei Verläufen (neueste zuerst) oft die richtige ist.
      if (richtung === 'auf') setRichtung('ab');
      else { setSortKey(null); setRichtung('auf'); }
      return;
    }
    setSortKey(key);
    setRichtung('auf');
  };

  const gefilterteZeilen = useMemo(() => {
    let liste = [...zeilen];

    const begriff = suche.trim().toLowerCase();
    if (begriff && suchfelder) {
      liste = liste.filter((z) =>
        suchfelder(z).some((f) => (f ?? '').toString().toLowerCase().includes(begriff)),
      );
    }

    for (const f of filter) {
      const wert = filterWerte[f.key];
      if (wert) liste = liste.filter((z) => f.passt(z, wert));
    }

    const spalte = spalten.find((s) => s.key === sortKey);
    if (spalte?.wert) {
      const faktor = richtung === 'auf' ? 1 : -1;
      liste.sort((a, b) => {
        const wa = spalte.wert!(a);
        const wb = spalte.wert!(b);
        // Leere Werte immer ans Ende, egal in welcher Richtung — sonst steht
        // beim Umschalten eine Wand aus Strichen vor den echten Werten.
        const aLeer = wa === null || wa === undefined || wa === '';
        const bLeer = wb === null || wb === undefined || wb === '';
        if (aLeer && bLeer) return 0;
        if (aLeer) return 1;
        if (bLeer) return -1;
        if (wa instanceof Date || wb instanceof Date) {
          return (new Date(wa as Date).getTime() - new Date(wb as Date).getTime()) * faktor;
        }
        if (typeof wa === 'number' && typeof wb === 'number') return (wa - wb) * faktor;
        return String(wa).localeCompare(String(wb), 'de', { numeric: true }) * faktor;
      });
    }

    return liste;
  }, [zeilen, suche, suchfelder, filter, filterWerte, spalten, sortKey, richtung]);

  const eingeschraenkt = Boolean(suche.trim()) || Object.values(filterWerte).some(Boolean);

  const zeigt = (key: string) => {
    const s = spalten.find((x) => x.key === key);
    if (!s || s.fest) return true;
    return spaltenWahl[key] ?? !s.versteckt;
  };
  const sichtbareSpalten = spalten.filter((s) => zeigt(s.key));
  const setSpalte = (key: string, an: boolean) =>
    setSpaltenWahl((w) => {
      const s = spalten.find((x) => x.key === key);
      const rest = { ...w };
      // Was der Vorgabe entspricht, wird nicht gemerkt — sonst bliebe eine
      // später neu eingeführte Vorgabe bei dieser Person wirkungslos.
      if (an === !s?.versteckt) delete rest[key];
      else rest[key] = an;
      return rest;
    });

  // Zurueck auf Seite 1, sobald sich die Menge oder die Reihenfolge aendert.
  // Ohne das steht man nach dem Tippen im Suchfeld auf einer Seite, die es
  // nicht mehr gibt, und die Tabelle wirkt leer.
  const abhaengigkeiten = `${suche}|${sortKey}|${richtung}|${Object.entries(filterWerte).sort().join()}`;
  useEffect(() => { setSeite(1); }, [abhaengigkeiten]);

  const aus = proSeite <= 0;
  const seiten = aus ? 1 : Math.max(1, Math.ceil(gefilterteZeilen.length / proSeite));
  // Geklemmt statt gespeichert: filtert jemand von 400 auf 3 Zeilen herunter,
  // waehrend er auf Seite 9 steht, greift der Reset oben erst nach dem Rendern.
  // Ohne diese Klemme zeigt die Tabelle fuer einen Bildaufbau nichts an.
  const seiteSicher = Math.min(Math.max(1, seite), seiten);
  const sichtbar = aus
    ? gefilterteZeilen
    : gefilterteZeilen.slice((seiteSicher - 1) * proSeite, seiteSicher * proSeite);

  return {
    sichtbar,
    gefiltert: gefilterteZeilen.length,
    alleGefilterten: gefilterteZeilen,
    gesamt: zeilen.length,
    blaettern: {
      seite: seiteSicher,
      seiten,
      setSeite,
      von: gefilterteZeilen.length === 0 ? 0 : (seiteSicher - 1) * proSeite + 1,
      bis: aus ? gefilterteZeilen.length : Math.min(seiteSicher * proSeite, gefilterteZeilen.length),
      gesamt: gefilterteZeilen.length,
      proSeite,
      setProSeite: (n: number) => { setProSeite(n); setSeite(1); },
    },
    suche,
    setSuche,
    sortKey,
    richtung,
    sortieren,
    filterWerte,
    setFilter: (key, wert) => setFilterWerte((f) => ({ ...f, [key]: wert })),
    zuruecksetzen: () => { setSuche(''); setFilterWerte({}); },
    eingeschraenkt,
    sichtbareSpalten,
    zeigt,
    setSpalte,
  };
}

/** Die Kopfzeile: klickbar, wo sortiert werden kann. */
export function TabellenKopf<T>({
  spalten,
  steuerung,
  filter = [],
  extra,
  vorne,
}: {
  spalten: SpaltenDefinition<T>[];
  steuerung: TabellenSteuerung<T>;
  /** Dieselben Filter wie in der Leiste — hier unter ihrer Spalte. */
  filter?: FilterDefinition<T>[];
  /** Spaltenüberschriften ohne Sortierung, etwa "Aktionen". */
  extra?: string[];
  /**
   * Eine Spalte ganz vorn, vor allen sortierbaren — für ein Auswahlkästchen.
   *
   * Getrennt von `extra`, weil die Seite eine Rolle spielt: ein Kästchen, das
   * hinter den Daten steht, findet niemand, und die Auswahl gehört links neben
   * das, was ausgewählt wird.
   */
  vorne?: ReactNode;
}) {
  return (
    <thead className="bg-slate-50">
      <tr>
        {vorne !== undefined && (
          <th className="w-8 px-3 py-3 text-left">{vorne}</th>
        )}
        {spalten.filter((s) => steuerung.zeigt(s.key)).map((s) => {
          const aktiv = steuerung.sortKey === s.key;
          const sortierbar = Boolean(s.wert);
          return (
            <th
              key={s.key}
              className={`px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap ${
                sortierbar ? 'cursor-pointer select-none hover:text-slate-700' : ''
              }`}
              onClick={sortierbar ? () => steuerung.sortieren(s.key) : undefined}
              // Für Tastaturbedienung: eine Kopfzelle, die klickt, muss auch
              // fokussierbar sein.
              tabIndex={sortierbar ? 0 : undefined}
              onKeyDown={
                sortierbar
                  ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); steuerung.sortieren(s.key); } }
                  : undefined
              }
              aria-sort={aktiv ? (steuerung.richtung === 'auf' ? 'ascending' : 'descending') : undefined}
            >
              <span className="inline-flex items-center gap-1">
                {s.label}
                {sortierbar && (
                  aktiv
                    ? (steuerung.richtung === 'auf' ? <ArrowUp size={12} /> : <ArrowDown size={12} />)
                    : <ArrowUpDown size={12} className="opacity-25" />
                )}
              </span>
            </th>
          );
        })}
        {extra?.map((h) => (
          <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">
            {h}
          </th>
        ))}
      </tr>

      {/* ══ FILTER IM KOPF, UNTER IHRER SPALTE ══════════════════════════════
          Die Filter gab es schon, aber nur in der Leiste über der Tabelle —
          losgelöst von der Spalte, auf die sie wirken. Bei acht Spalten muss
          man dann raten, welcher Auswahlkasten zu welcher Überschrift gehört.

          Hier stehen sie darunter. `filter` bleibt optional: Tabellen, die
          keine mitgeben, bekommen die Zeile gar nicht — eine leere Reihe grauer
          Kästen wäre schlechter als keine. */}
      {filter.length > 0 && (
        <tr className="bg-slate-50/60">
          {vorne !== undefined && <th className="px-3 pb-2" />}
          {spalten.filter((s) => steuerung.zeigt(s.key)).map((s) => {
            const f = filter.find((x) => x.key === s.key);
            return (
              <th key={s.key} className="px-4 pb-2 text-left font-normal">
                {f ? (
                  <select
                    value={steuerung.filterWerte[f.key] ?? ''}
                    onChange={(e) => steuerung.setFilter(f.key, e.target.value)}
                    aria-label={`${s.label} filtern`}
                    className={`w-full rounded border px-1.5 py-1 text-xs ${
                      steuerung.filterWerte[f.key]
                        ? 'border-brand-400 bg-brand-50 text-brand-800 font-medium'
                        : 'border-slate-200 bg-surface text-slate-500'
                    }`}
                  >
                    <option value="">alle</option>
                    {f.optionen.map((o) => (
                      <option key={o.wert} value={o.wert}>{o.label}</option>
                    ))}
                  </select>
                ) : null}
              </th>
            );
          })}
          {extra?.map((h) => <th key={h} className="px-4 pb-2" />)}
        </tr>
      )}
    </thead>
  );
}

/**
 * Welche Spalten zu sehen sind — je Person gemerkt, wenn die Tabelle
 * `speichern` gesetzt hat.
 *
 * Die Instanzliste ist an dem Punkt, wo nicht mehr jede Spalte für jeden
 * zählt: wer Sicherheitsstände prüft, will den Tech-Stack, wer betreut, will
 * sehen, wer zuletzt etwas geändert hat. Beides immer zu zeigen, macht die
 * Tabelle für beide breiter als nötig.
 */
export function SpaltenWahl<T>({ spalten, steuerung }: {
  spalten: SpaltenDefinition<T>[];
  steuerung: TabellenSteuerung<T>;
}) {
  const [offen, setOffen] = useState(false);
  const wahlbar = spalten.filter((s) => !s.fest);
  useEffect(() => {
    if (!offen) return;
    const zu = (e: KeyboardEvent) => { if (e.key === 'Escape') setOffen(false); };
    window.addEventListener('keydown', zu);
    return () => window.removeEventListener('keydown', zu);
  }, [offen]);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOffen((o) => !o)}
        aria-expanded={offen}
        className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-surface px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
      >
        <Columns3 size={15} /> Spalten
      </button>
      {offen && (
        <>
          {/* Klick daneben schließt. */}
          <div className="fixed inset-0 z-10" onClick={() => setOffen(false)} aria-hidden />
          <div className="absolute right-0 z-20 mt-1 w-56 rounded-lg border border-slate-200 bg-surface p-2 shadow-lg">
            {wahlbar.map((s) => (
              <label key={s.key} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm text-slate-700 hover:bg-slate-50">
                <input
                  type="checkbox"
                  checked={steuerung.zeigt(s.key)}
                  onChange={(e) => steuerung.setSpalte(s.key, e.target.checked)}
                />
                {s.label}
              </label>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/** Suchfeld und Auswahlfilter über der Tabelle. */
export function TabellenLeiste<T>({
  steuerung,
  filter = [],
  platzhalter,
  kinder,
}: {
  steuerung: TabellenSteuerung<T>;
  filter?: FilterDefinition<T>[];
  /** Ohne Angabe steht das allgemeine "Suchen…" da. */
  platzhalter?: string;
  /** Zusätzliche Bedienelemente rechts, etwa ein "Neu"-Knopf. */
  kinder?: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap items-center gap-2 mb-3">
      <div className="relative">
        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          value={steuerung.suche}
          onChange={(e) => steuerung.setSuche(e.target.value)}
          placeholder={platzhalter ?? t('tables.search')}
          className="pl-8 pr-3 py-1.5 text-sm border border-slate-300 rounded-lg w-64 focus:outline-none focus:ring-2 focus:ring-brand-500"
        />
      </div>

      {filter.map((f) => (
        <select
          key={f.key}
          value={steuerung.filterWerte[f.key] ?? ''}
          onChange={(e) => steuerung.setFilter(f.key, e.target.value)}
          className="py-1.5 px-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500"
        >
          <option value="">{f.label}: {t('tables.filterAll')}</option>
          {f.optionen.map((o) => (
            <option key={o.wert} value={o.wert}>{o.label}</option>
          ))}
        </select>
      ))}

      {steuerung.eingeschraenkt && (
        <button
          type="button"
          onClick={steuerung.zuruecksetzen}
          className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
        >
          <X size={14} />
          {t('tables.clearFilters', { shown: steuerung.gefiltert, total: steuerung.gesamt })}
        </button>
      )}

      <div className="ml-auto flex items-center gap-2">{kinder}</div>
    </div>
  );
}

/** Wieviele Seitenknoepfe hoechstens nebeneinander stehen. */
const FENSTER = 5;

/**
 * Die Blätterleiste unter einer Tabelle.
 *
 * Nimmt einfache Werte statt einer Tabellensteuerung, damit das serverseitig
 * geblätterte Audit-Log dieselbe Leiste benutzen kann. Vorher hatte es eine
 * eigene, und die war doppelt falsch: sie las die Gesamtzahl an der falschen
 * Stelle des Umschlags (`total` statt `pagination.total`), rechnete daraus
 * null Seiten und hat den Weiter-Knopf dauerhaft ausgegraut.
 *
 * Die Leiste erscheint nur, wenn es etwas zu blättern gibt. Eine Leiste unter
 * einer Tabelle mit vier Zeilen ist Lärm.
 */
export function Blaetterleiste({ blaettern, groessen = [25, 50, 100, 200] }: {
  blaettern: Blaettern;
  groessen?: number[];
}) {
  const { t } = useTranslation();
  const { seite, seiten, setSeite, von, bis, gesamt, proSeite, setProSeite } = blaettern;

  if (seiten <= 1) return null;

  // Ein Fenster um die aktuelle Seite, das an den Raendern nicht ueber die
  // Grenzen laeuft: bei Seite 1 von 40 sollen 1-5 stehen, bei Seite 40 die
  // Seiten 36-40 -- nicht 38-42.
  const breite = Math.min(FENSTER, seiten);
  const erste = Math.max(1, Math.min(seite - Math.floor(breite / 2), seiten - breite + 1));
  const nummern = Array.from({ length: breite }, (_, i) => erste + i);

  const knopf = 'px-2.5 py-1 text-sm rounded-lg border border-slate-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-50';

  return (
    <div className="flex flex-wrap items-center gap-2 mt-3">
      <span className="text-sm text-slate-500">
        {t('tables.range', { von, bis, gesamt })}
      </span>

      {setProSeite && (
        <select
          value={proSeite}
          onChange={(e) => setProSeite(Number(e.target.value))}
          className="py-1 px-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500"
          aria-label={t('tables.perPage')}
        >
          {groessen.map((g) => (
            <option key={g} value={g}>{t('tables.perPageOption', { n: g })}</option>
          ))}
        </select>
      )}

      <div className="ml-auto flex items-center gap-1">
        <button type="button" className={knopf} onClick={() => setSeite(seite - 1)}
                disabled={seite <= 1} aria-label={t('tables.prevPage')}>
          <ChevronLeft size={14} />
        </button>

        {erste > 1 && <span className="px-1 text-sm text-slate-400">…</span>}

        {nummern.map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => setSeite(n)}
            aria-current={n === seite ? 'page' : undefined}
            className={`px-2.5 py-1 text-sm rounded-lg border ${
              n === seite
                ? 'border-brand-500 bg-brand-50 text-brand-700 font-semibold'
                : 'border-slate-300 hover:bg-slate-50'
            }`}
          >
            {n}
          </button>
        ))}

        {erste + breite - 1 < seiten && <span className="px-1 text-sm text-slate-400">…</span>}

        <button type="button" className={knopf} onClick={() => setSeite(seite + 1)}
                disabled={seite >= seiten} aria-label={t('tables.nextPage')}>
          <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
}
